import { Badge, Inline, PictogramButton } from '@amboss/design-system';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { trackEvent } from '../../../../analytics';
import { config, ServerResponseErrorCode } from '../../../../config';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { useModal } from '../../../../hooks/useModal';
import useProfile from '../../../../hooks/useProfile';
import { useSocketEmit } from '../../../../hooks/useSocketEmit';
import {
  type Credit,
  CreditAssociation,
  CreditType,
} from '../../../../interfaces/credit';
import type { Credits } from '../../../../interfaces/credits';
import {
  creditsTypeSelector,
  updateCredit,
} from '../../../../store/credits/creditsSlice';
import { download, isCourseCreditType } from '../../../../utils';
import ClaimedItem from '../../../atoms/ClaimedItem';
import ItemCourse from '../../../atoms/ItemCourse';
import ItemDate from '../../../atoms/ItemDate';
import ItemNejm from '../../../atoms/ItemNejm';
import CourseCertificateModal from '../../../modals/CourseCertificateModal';
import TabList from '../tabs/TabList';

declare type ClaimedListProps = {
  credits: Credits;
};
const ClaimedList = ({ credits }: ClaimedListProps) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('token');
  const { parsResubmit } = useSocketEmit();
  const dispatch = useAppDispatch();
  const creditType = useAppSelector(creditsTypeSelector);
  const { openModal, closeModal } = useModal();
  const handleClick = (item: Credit) => {
    const errorString = t('credits.tabs.tab3.download.error');
    trackEvent({
      event: config.analytics.events.CME_PDF_DOWNLOADED,
      options: {
        xid: profile?.xid,
        cme_activity: config.analytics.activities[creditType],
      },
    });

    // Open certificate page in new tab for CCCR and E2I credit types
    if (isCourseCreditType(creditType)) {
      const courseId = item.originIds?.[0];

      if (courseId) {
        return openModal({
          onClose: closeModal,
          modalMaxHeight: '90vh',
          children: <CourseCertificateModal courseId={courseId} />,
        });
      }
    } else {
      // Use existing download for other credit types
      download({
        api: config.api.endpoints.download(item.id),
        fileName: config.api.fileName(item.id),
        accessToken: accessToken || undefined,
        errorString,
      });
    }
  };

  const formattedDate = (item: any) => {
    if (isCourseCreditType(creditType)) return moment(item.endDate).format('MMM D, YYYY');

    return `${moment(item.startDate).format('MMMM D, YYYY')} - ${moment(
      item.endDate,
    ).format('MMMM D, YYYY')}`;
  };

  const getItemElement = (item: any) => {
    if (isCourseCreditType(creditType))
      return (
        <ItemCourse
          courseId={item.id}
          courseTitle={item.sourceData[0].courseTitles[0]}
          courseSubtitle={item.sourceData[0].courseSubtitles[0]}
          parsStatus={item.parsStatus}
          parsResponse={item.parsResponse}
        />
      );

    return creditType === CreditType.IPC ? (
      <ClaimedItem
        association={item.association}
        parsStatus={item.parsStatus}
        parsResponse={item.parsResponse}
      />
    ) : (
      <ItemNejm
        itemValue={item.originIds.length}
        parsStatus={item.parsStatus}
        parsResponse={item.parsResponse}
      />
    );
  };

  const resubmitItem = async (itemId: string) => {
    try {
      const credit = await parsResubmit(itemId); //.catch((e) => e);
      if (credit) {
        dispatch(updateCredit(credit));
      }
    } catch (err: any) {
      // TODO: Show toast after we bump the @amboss/design-system dependency
      const errorCode = err?.result?.error?.code;

      if (errorCode === ServerResponseErrorCode.ParsSubmissionDisallowed) {
        alert(t('credits.tabs.tab3.resubmit.error.parsSubmissionDisallowed'));
        return;
      }

      alert(t('general.error'));
    }
  };

  const resubmitButton = (item: Credit) => {
    const isNotSubmittedToPars = item.parsStatus === config.parsStatus.NOT_SUBMITTED;
    const isParsRecoverableError =
      item.parsResponse?.errorCode &&
      config.parsRecoverableErrorCodes.includes(item.parsResponse?.errorCode);

    if (isNotSubmittedToPars || isParsRecoverableError) {
      return (
        <PictogramButton
          icon='reset'
          label={t('credits.tabs.tab3.listItem.resubmit') || ''}
          onClick={() => resubmitItem(item.id)}
          size='s'
          type='button'
          variant='secondary'
        />
      );
    }
  };

  return (
    <>
      {credits?.items.map((item, i) => {
        return (
          <TabList
            key={item.id}
            left={<ItemDate date={formattedDate(item)} />}
            leftSize={[12, 4, 4]}
            centerSize={[12, 5, 5]}
            center={getItemElement(item)}
            right={
              <Inline alignItems={'right'} vAlignItems={'center'} noWrap>
                <Badge
                  color={'brand'}
                  text={t('credits.tabs.tab2.listItem.badge', {
                    count: item.value,
                    type:
                      item.association === CreditAssociation.AHPRA &&
                      creditType === CreditType.IPC
                        ? 'HOUR'
                        : 'CREDIT',
                  })}
                />
                {resubmitButton(item)}
                <PictogramButton
                  icon='download'
                  label={
                    t('credits.tabs.tab3.listItem.button', { count: item.value }) || ''
                  }
                  onClick={() => handleClick(item)}
                  size='s'
                  type='button'
                  variant='secondary'
                />
                {/*<Button variant={'secondary'} leftIcon={"download"}*/}
                {/*        onClick={() => handleClick(item)}>{t('credits.tabs.tab3.listItem.button', {count: item.value})}</Button>*/}
              </Inline>
            }
            isLastItem={credits.items.length - 1 === i}
          />
        );
      })}
    </>
  );
};
export default ClaimedList;
